import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { RenderProps } from '../lib/slotProps';
import type { EndCardOption } from './useTemplates';

/** @deprecated Use CreateJobParams.props instead. Kept for backward compatibility. */
export interface AssetMapping {
  slotId: string;
  storageUrl?: string;
  type: string;
  textValue?: string;
}

export interface CreateJobParams {
  templateId: string;
  /** Human-readable label shown in job history. Falls back to deliverableId then templateId. */
  name?: string;
  /** Semver string from the template definition. */
  templateVersion?: string;
  /** Remotion composition ID — must match the registered composition name exactly. */
  remotionComponentId?: string;
  /** New structured props keyed by slot_id. Required for all new jobs. */
  props: RenderProps;
  /** @deprecated Legacy flat mappings. Kept only for backward compat during transition. */
  assetMappings?: AssetMapping[];
  market?: string;
  /**
   * Camel-case field names follow the existing jobs Firestore collection convention
   * (templateId, templateVersion, remotionComponentId, …).
   * Template documents use snake_case to mirror the spec schema (§4).
   */
  /** Deliverable format identifier (e.g. "V1", "veo_shorts_v1"). */
  deliverableId?: string;
  /** High-level format category. */
  format?: 'shorts_featured_video' | 'full_screen_interstitial';
  /** Output container format for the Remotion render. */
  outputFormat?: 'mp4' | 'png';
  /**
   * ID of the selected pre-rendered end card (matches an EndCardOption.id on the template).
   * When set, the rendering pipeline uses the corresponding pre-rendered video instead of
   * the legacy editable end-frame slots.
   */
  endCardId?: string;
  /**
   * BCP 47 language tag for this job, e.g. "en", "ja".
   * Used to auto-select the appropriate endCardOption when endCardId is not provided.
   */
  language?: string;
  /**
   * Available end card options from the selected template.
   * Passed only for pre-submission validation — not persisted to Firestore.
   * When provided alongside endCardId, createJob will throw if the id is not found.
   */
  endCardOptions?: EndCardOption[];
  /**
   * How the job was initiated.
   * `'manual'` — created by a user through the Template Stamper UI (default).
   * `'pipeline'` — created programmatically by an external/automated flow.
   */
  jobSource?: 'manual' | 'pipeline';
  /** CI/CD run identifier for the originating pipeline execution. Pipeline jobs only. */
  runId?: string;
  /** Path or name of the pipeline definition that triggered this job. Pipeline jobs only. */
  pipelinePath?: string;
  /** Selected effect group metadata (Template 2 grouped effects). */
  effectGroupId?: string;
  effectGroupLabel?: string;
  effectOptionId?: string;
  effectOptionLabel?: string;
  effectOptionValue?: string;
  effectOptionAssetUrl?: string;
}

export interface Job {
  id: string;
  templateId: string;
  /** Human-readable label shown in job history. */
  name?: string;
  /** Structured render props keyed by slot_id. */
  props: RenderProps;
  status: 'queued' | 'preprocessing' | 'rendering' | 'completed' | 'failed';
  progress: number;
  createdAt: Date;
  market?: string;
  /** Camel-case — matches jobs Firestore collection convention. */
  deliverableId?: string;
  format?: 'shorts_featured_video' | 'full_screen_interstitial';
  outputFormat?: 'mp4' | 'png';
  /** ID of the selected pre-rendered end card. */
  endCardId?: string;
  /** BCP 47 language tag for this job, e.g. "en". */
  language?: string;
  /**
   * How the job was initiated.
   * `'manual'` — created by a user through the Template Stamper UI (default).
   * `'pipeline'` — created programmatically by an external/automated flow.
   */
  jobSource: 'manual' | 'pipeline';
  /** CI/CD run identifier for the originating pipeline execution. Pipeline jobs only. */
  runId?: string | null;
  /** Path or name of the pipeline definition that triggered this job. Pipeline jobs only. */
  pipelinePath?: string | null;
  effectGroupId?: string | null;
  effectGroupLabel?: string | null;
  effectOptionId?: string | null;
  effectOptionLabel?: string | null;
  effectOptionValue?: string | null;
  effectOptionAssetUrl?: string | null;
}

export function useJobCreate() {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createJob = async (params: CreateJobParams): Promise<string> => {
    try {
      setCreating(true);
      setError(null);

      // Create job document in Firestore with new props structure.
      // assetMappings is written alongside for backward compat with any existing
      // cloud function code that still reads it.

      // Validate endCardId against the available options before any write.
      if (params.endCardId && params.endCardOptions) {
        const match = params.endCardOptions.find((opt) => opt.id === params.endCardId);
        if (!match) {
          throw new Error(
            `endCardId "${params.endCardId}" is not a valid option for this template. ` +
            `Available ids: ${params.endCardOptions.map((o) => o.id).join(', ')}`
          );
        }
        if (params.language && match.language !== params.language) {
          throw new Error(
            `endCardId "${params.endCardId}" is for language "${match.language}" ` +
            `but job language is "${params.language}".`
          );
        }
      }

      const jobRef = await addDoc(collection(db, 'jobs'), {
        templateId: params.templateId,
		name: params.name ?? params.deliverableId ?? params.templateId,
        templateVersion: params.templateVersion ?? null,
        remotionComponentId: params.remotionComponentId ?? null,
        props: params.props,
        // Backward compat — will be removed once triggerRender fully migrated
        assetMappings: params.assetMappings ?? null,
        market: params.market || 'default',
        deliverableId: params.deliverableId ?? 'veo_shorts_v1',
        format: params.format ?? 'shorts_featured_video',
        outputFormat: params.outputFormat ?? 'mp4',
        endCardId: params.endCardId ?? null,
        language: params.language ?? null,
        jobSource: params.jobSource ?? 'manual',
        runId: params.runId ?? null,
        pipelinePath: params.pipelinePath ?? null,
        effectGroupId: params.effectGroupId ?? null,
        effectGroupLabel: params.effectGroupLabel ?? null,
        effectOptionId: params.effectOptionId ?? null,
        effectOptionLabel: params.effectOptionLabel ?? null,
        effectOptionValue: params.effectOptionValue ?? null,
        effectOptionAssetUrl: params.effectOptionAssetUrl ?? null,
        status: 'queued',
        progress: 0,
        attempt: 0,
        maxAttempts: 3,
        retryCount: 0,
        lastError: null,
        retriedAt: null,
        createdAt: serverTimestamp(),
      });

      return jobRef.id;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create job';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return {
    createJob,
    creating,
    error,
  };
}
